import React, { useEffect, useState } from 'react';
import { GenerateReturn, ScheduleReturn } from '../clients/rust-api-client';
import { billingService } from '../clients/ApiClients';
import { Button } from '../common/Catalyst/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from '../common/Catalyst/dialog';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '../common/Catalyst/fieldset';
import { Input } from '../common/Catalyst/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../common/Catalyst/table';
import { Text } from '../common/Catalyst/text';
import { FieldPicker } from '../common/Custom/FieldPicker';
import { Subheading } from '../common/Catalyst/heading';
import ShowAlert from '../ShowAlert';
import {
  billingErrorMessage,
  formatBillingDate,
  formatMoney,
  sumMoney,
} from '../utils/billingHelpers';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onGenerated: () => void;
  schedules: ScheduleReturn[];
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Generate the pending runs for a schedule.
 *
 * **Generating charges nobody.** It writes reviewable run rows and
 * their lines; the ledger is untouched until each run is posted, which
 * is a separate act by a stricter role.
 *
 * **Catch-up is the case to get right.** Asking for a `through_date`
 * well in the past produces one run per missed period, and back-billing
 * a year on purpose is legitimate while doing it by accident is not.
 * So the result lists every period it created with its own total and a
 * prominent count — twelve rows and a total is the difference between
 * the two.
 */
const GenerateRunsDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  onGenerated,
  schedules,
}) => {
  const active = schedules.filter((s) => s.status === 'active');
  const [chosenId, setChosenId] = useState('');
  // Derived rather than synced. This dialog is mounted closed before
  // the schedules have loaded, so a default held in state has to be
  // kept in step with a prop that arrives later; deriving it cannot
  // fall out of step, and it also survives a schedule being ended
  // while the dialog is open.
  const scheduleId =
    chosenId && active.some((s) => s.id === chosenId)
      ? chosenId
      : (active[0]?.id ?? '');
  const setScheduleId = setChosenId;
  const [through, setThrough] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [running, setRunning] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [result, setResult] = useState<GenerateReturn | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setChosenId('');
    setThrough('');
    setErrors({});
    setFailure(null);
    setResult(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const generate = async (): Promise<void> => {
    const found: Record<string, string> = {};
    if (!scheduleId) found.scheduleId = 'Pick a schedule.';
    if (through && !DATE.test(through)) {
      found.through = 'Use a date, or leave blank for today.';
    }
    setErrors(found);
    if (Object.keys(found).length > 0) return;

    try {
      setRunning(true);
      setFailure(null);
      const { data } = await billingService.generateRuns({
        scheduleId,
        generateRequest: { through_date: through || null },
      });
      setResult(data);
      onGenerated();
    } catch (err) {
      console.error('Error generating runs:', err);
      setFailure(billingErrorMessage(err));
    } finally {
      setRunning(false);
    }
  };

  const chosen = active.find((s) => s.id === scheduleId);

  return (
    <Dialog open={isOpen} onClose={onClose} size="3xl">
      <DialogTitle>Generate billing runs</DialogTitle>
      <DialogBody>
        {failure && (
          <div className="mb-4">
            <ShowAlert
              alertType="error"
              alertTitle="Nothing was generated"
              alertMessage={failure}
              isVisible={!!failure}
              onClose={() => setFailure(null)}
              timeout={14000}
              isInline
            />
          </div>
        )}

        {result ? (
          <>
            {result.is_catch_up ? (
              <div className="mb-4 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 p-4">
                <Subheading>
                  {result.periods_generated} periods, not one
                </Subheading>
                <Text className="mt-1">
                  This is a catch-up. Every period below is a separate run and{' '}
                  <strong>none of them has charged anybody yet</strong> — each
                  has to be posted. Check the list is what you meant before
                  posting any of it.
                </Text>
              </div>
            ) : (
              <Text className="mb-4">
                One period generated. Nothing has reached a homeowner&rsquo;s
                ledger — post it when you have looked at the lines.
              </Text>
            )}

            <Table dense>
              <TableHead>
                <TableRow>
                  <TableHeader>Period</TableHeader>
                  <TableHeader>Due</TableHeader>
                  <TableHeader className="text-right">Homes</TableHeader>
                  <TableHeader className="text-right">Total</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.runs.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>
                      {formatBillingDate(r.period_start)} –{' '}
                      {formatBillingDate(r.period_end)}
                    </TableCell>
                    <TableCell>{formatBillingDate(r.due_date)}</TableCell>
                    <TableCell className="text-right">
                      {r.charge_count}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatMoney(r.total_amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Text className="mt-3">
              <strong>
                {formatMoney(sumMoney(result.runs.map((r) => r.total_amount)))}
              </strong>{' '}
              across {result.periods_generated}{' '}
              {result.periods_generated === 1 ? 'period' : 'periods'}, still
              unposted.
            </Text>
          </>
        ) : (
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Schedule</Label>
                <Description>
                  Only active schedules generate. A draft one bills nothing by
                  design.
                </Description>
                <FieldPicker
                  label="Schedule"
                  placeholder={
                    active.length === 0 ? 'No active schedule' : undefined
                  }
                  value={scheduleId}
                  onChange={setScheduleId}
                  options={active.map((s) => ({
                    value: s.id,
                    label: `${s.name} (${formatMoney(s.amount)})`,
                  }))}
                />
                {errors.scheduleId && (
                  <ErrorMessage>{errors.scheduleId}</ErrorMessage>
                )}
              </Field>

              <Field>
                <Label>Through</Label>
                <Description>
                  Generate every period up to this date. Blank means today. A
                  date well in the past produces one run{' '}
                  <em>per missed period</em> — which is how you back-bill on
                  purpose, and how you back-bill by accident.
                </Description>
                <Input
                  type="date"
                  value={through}
                  invalid={!!errors.through}
                  onChange={(e) => setThrough(e.target.value)}
                  className="max-w-[14rem]"
                />
                {errors.through && (
                  <ErrorMessage>{errors.through}</ErrorMessage>
                )}
              </Field>

              {chosen && (
                <Text>
                  Periods step from{' '}
                  {formatBillingDate(chosen.first_period_start)}, the
                  schedule&rsquo;s anchor. Generating writes nothing to any
                  homeowner&rsquo;s ledger — you will see the totals first.
                </Text>
              )}
            </FieldGroup>
          </Fieldset>
        )}
      </DialogBody>
      <DialogActions>
        {result ? (
          <Button color="sky" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button plain onClick={onClose} disabled={running}>
              Cancel
            </Button>
            <Button
              color="sky"
              onClick={generate}
              disabled={running || active.length === 0}
            >
              {running ? 'Generating…' : 'Generate'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default GenerateRunsDialog;
